import type { ChangeEvent, ChangeEventHandler } from 'react'
import { useCallback, useEffect, useMemo, useReducer } from 'react'

import type { Selectable } from '@loadsmart/loadsmart-ui/dist/hooks/useSelectable'
import type EventLike from '@loadsmart/loadsmart-ui/dist/utils/types/EventLike'
import type { TextFieldProps } from '@loadsmart/miranda-react'

import type { Option } from 'core/domain/Option'

import {
  CHARGE_DESCRIPTION_MAX_CHARS,
  calculateTotalPrice,
  validateAttachmentFile,
  type NewShipmentCharge,
  type AccessorialOption,
} from '../../domain/ShipmentCharges'

export type NewChargeState = Omit<NewShipmentCharge, 'chargeType' | 'priceItemType'> & {
  accessorial: AccessorialOption | null
  totalPrice: number
  errors: Record<
    'accessorial' | 'unitPrice' | 'stop' | 'description' | 'fileSize' | 'fileFormat',
    boolean
  >
}

type NewChargeAction =
  | { type: 'SET_ACCESSORIAL'; accessorial: NewChargeState['accessorial'] }
  | { type: 'SET_QUANTITY'; quantity: NewChargeState['quantity'] }
  | { type: 'SET_UNIT_PRICE'; unitPrice: NewChargeState['unitPrice'] }
  | { type: 'SET_STOP'; stop: NewChargeState['stop'] }
  | { type: 'SET_DESCRIPTION'; description: NewChargeState['description'] }
  | { type: 'ADD_ATTACHMENT'; file: File }
  | { type: 'REMOVE_ATTACHMENT' }
  | { type: 'RESET_STATE' }

const initialNewChargeState: NewChargeState = {
  accessorial: null,
  quantity: 1,
  unitPrice: undefined,
  totalPrice: 0,
  stop: null,
  description: '',
  attachmentFile: undefined,
  errors: {
    accessorial: true,
    unitPrice: true,
    stop: true,
    description: false,
    fileSize: false,
    fileFormat: false,
  },
}

function newChargeReducer(state: NewChargeState, action: NewChargeAction): NewChargeState {
  switch (action.type) {
    case 'SET_ACCESSORIAL': {
      return {
        ...state,
        accessorial: action.accessorial,
        errors: { ...state.errors, accessorial: !action.accessorial },
      }
    }
    case 'SET_QUANTITY': {
      return {
        ...state,
        quantity: action.quantity,
        totalPrice: calculateTotalPrice({ quantity: action.quantity, unitPrice: state.unitPrice }),
      }
    }
    case 'SET_UNIT_PRICE': {
      return {
        ...state,
        unitPrice: action.unitPrice,
        totalPrice: calculateTotalPrice({ quantity: state.quantity, unitPrice: action.unitPrice }),
        errors: { ...state.errors, unitPrice: !action.unitPrice },
      }
    }
    case 'SET_STOP': {
      return {
        ...state,
        stop: action.stop,
        errors: { ...state.errors, stop: !action.stop },
      }
    }
    case 'SET_DESCRIPTION': {
      return {
        ...state,
        description: action.description,
        errors: {
          ...state.errors,
          description: action.description.length > CHARGE_DESCRIPTION_MAX_CHARS,
        },
      }
    }
    case 'ADD_ATTACHMENT': {
      const { fileSizeIsAllowed, extensionIsAllowed } = validateAttachmentFile(action.file)

      return {
        ...state,
        attachmentFile: action.file,
        errors: {
          ...state.errors,
          fileSize: !fileSizeIsAllowed,
          fileFormat: !extensionIsAllowed,
        },
      }
    }
    case 'REMOVE_ATTACHMENT': {
      return {
        ...state,
        attachmentFile: undefined,
        errors: {
          ...state.errors,
          fileSize: false,
          fileFormat: false,
        },
      }
    }
    case 'RESET_STATE': {
      return initialNewChargeState
    }
    default: {
      return state
    }
  }
}

type useNewChargeFormParams = { isOpen: boolean }

export function useNewChargeForm({ isOpen }: useNewChargeFormParams) {
  const [{ errors, ...state }, dispatch] = useReducer(newChargeReducer, initialNewChargeState)

  const hasErrors = useMemo(() => Object.values(errors).some(error => error), [errors])

  useEffect(() => {
    dispatch({ type: 'RESET_STATE' })
  }, [isOpen])

  function onChargeTypeChange(event: EventLike<Selectable | Selectable[] | null>) {
    dispatch({
      type: 'SET_ACCESSORIAL',
      accessorial: event.target.value as AccessorialOption,
    })
  }

  function onQuantityChange(event: Parameters<NonNullable<TextFieldProps['onInput']>>[0]) {
    dispatch({
      type: 'SET_QUANTITY',
      quantity: Number((event as unknown as ChangeEvent<HTMLInputElement>).target.value),
    })
  }

  function onUnitPriceChange(event: Parameters<NonNullable<TextFieldProps['onInput']>>[0]) {
    dispatch({
      type: 'SET_UNIT_PRICE',
      unitPrice: Number((event as unknown as ChangeEvent<HTMLInputElement>).target.value),
    })
  }

  const setSelectedStop = useCallback((stop: Option<string | number> | null) => {
    dispatch({ type: 'SET_STOP', stop })
  }, [])

  function onStopChange(event: EventLike<Selectable | Selectable[] | null>) {
    setSelectedStop(event.target.value as Option<string>)
  }

  function onDescriptionChange(event: Parameters<ChangeEventHandler<HTMLTextAreaElement>>[0]) {
    dispatch({
      type: 'SET_DESCRIPTION',
      description: event.target.value,
    })
  }

  function onFileInputChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]

    if (!file) {
      return
    }
    dispatch({ type: 'ADD_ATTACHMENT', file })
  }

  function onRemoveAttachment() {
    dispatch({ type: 'REMOVE_ATTACHMENT' })
  }

  return {
    state,
    errors,
    hasErrors,
    onChargeTypeChange,
    onQuantityChange,
    onUnitPriceChange,
    setSelectedStop,
    onStopChange,
    onDescriptionChange,
    onFileInputChange,
    onRemoveAttachment,
  }
}
